import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Download, FileSpreadsheet, Trash2, Eye, X, Package,
  Loader2, Inbox, FileText, Mail, Phone, CalendarDays,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { formsApi, downloadFile } from "@/lib/api";
import { FilePreview } from "@/components/FilePreview";
import { isFileType } from "@/lib/formFields";
import { formatShortDate } from "@/lib/helpers";
import { useCan } from "@/context/AuthContext";

const STATUS_STYLES = {
  submitted: "bg-emerald-100 text-emerald-800",
  reviewed: "bg-blue-100 text-blue-800",
  flagged: "bg-amber-100 text-amber-800",
};

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "object") return Object.values(v).filter(Boolean).join(", ") || "—";
  return String(v);
}

export default function FormResponses() {
  const canDelete = useCan("delete");
  const { id, formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([formsApi.get(formId), formsApi.submissions(formId)]);
      setForm(f);
      setSubs(s);
    } catch {
      toast.error("Failed to load responses");
      navigate(`/events/${id}/forms`);
    } finally {
      setLoading(false);
    }
  }, [id, formId, navigate]);

  useEffect(() => { load(); }, [load]);

  const fieldMap = useMemo(() => Object.fromEntries((form?.fields || []).map((f) => [f.id, f])), [form]);
  const nonFileFields = (form?.fields || []).filter((f) => !isFileType(f.type));
  const fileFields = (form?.fields || []).filter((f) => isFileType(f.type));

  const filtered = useMemo(() => {
    let list = [...subs];
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((x) =>
        [x.code, x.guest_name, x.guest_email, x.guest_phone].some((v) => (v || "").toLowerCase().includes(s))
      );
    }
    list.sort((a, b) => (sort === "new" ? b.created_at.localeCompare(a.created_at) : a.created_at.localeCompare(b.created_at)));
    return list;
  }, [subs, q, sort]);

  const exportFile = async (ext) => {
    setExporting(true);
    try {
      await downloadFile(`/forms/${formId}/export.${ext}`, `${form.slug}-responses.${ext}`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const subFiles = (s) => {
    const out = [];
    Object.entries(s.files || {}).forEach(([fid, arr]) => {
      (arr || []).forEach((f) => out.push({ ...f, label: fieldMap[fid]?.label || "File" }));
    });
    return out;
  };

  const downloadSubZip = async (s) => {
    const files = subFiles(s).map((f) => ({ url: f.url, filename: f.filename, folder: `${s.code} ${s.guest_name || ""}`.trim() }));
    if (!files.length) { toast.message("No files in this submission"); return; }
    try {
      await downloadFile(`/documents/download-zip`, `${s.code}-files.zip`, { method: "post", data: { files } });
    } catch { toast.error("Download failed"); }
  };

  const setStatus = async (s, status) => {
    await formsApi.updateSubmission(formId, s.id, { status });
    setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, status } : x)));
    if (detail?.id === s.id) setDetail({ ...detail, status });
  };

  const del = async (s) => {
    if (!window.confirm(`Delete submission ${s.code}?`)) return;
    await formsApi.deleteSubmission(formId, s.id);
    toast.success("Submission deleted");
    setDetail(null);
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-5 py-8 sm:px-8">
        <button onClick={() => navigate(`/events/${id}/forms`)} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-forms">
          <ArrowLeft className="h-4 w-4" /> Back to forms
        </button>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Responses</p>
            <h1 className="mt-1 font-serif text-3xl font-semibold">{form?.name || "Form"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subs.length} submission{subs.length === 1 ? "" : "s"}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => exportFile("xlsx")} disabled={exporting || !subs.length} data-testid="export-xlsx" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"><FileSpreadsheet className="h-4 w-4" /> Export Excel</button>
            <button onClick={() => exportFile("csv")} disabled={exporting || !subs.length} data-testid="export-csv" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2.5 text-sm font-semibold disabled:opacity-50"><Download className="h-4 w-4" /> Export CSV</button>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ID, name, email or phone" data-testid="responses-search" className="w-full rounded-xl border border-input bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/20" />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="responses-sort" className="rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:border-gold">
            <option value="new">Newest first</option>
            <option value="old">Oldest first</option>
          </select>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-border bg-card">
          {loading ? (
            <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : filtered.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-center text-sm text-muted-foreground">
              <Inbox className="mb-2 h-8 w-8" /> No responses yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="responses-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3">Submission ID</th>
                    <th className="px-4 py-3">Guest</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Submitted</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid={`sub-row-${s.id}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-gold">{s.code}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{s.guest_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{s.guest_email || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{s.guest_phone || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatShortDate((s.created_at || "").slice(0, 10))}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_STYLES[s.status] || STATUS_STYLES.submitted}`}>{s.status}</span></td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setDetail(s)} data-testid={`view-sub-${s.id}`} title="View" className="rounded-lg border border-border p-2 hover:bg-secondary"><Eye className="h-4 w-4" /></button>
                          <button onClick={() => downloadSubZip(s)} data-testid={`zip-sub-${s.id}`} title="Download files" className="rounded-lg border border-border p-2 hover:bg-secondary"><Package className="h-4 w-4" /></button>
                          {canDelete && (<button onClick={() => del(s)} data-testid={`del-sub-${s.id}`} title="Delete" className="rounded-lg border border-border p-2 text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sub-detail-modal">
            <div className="flex items-start justify-between">
              <div>
                <span className="font-mono text-lg font-bold text-gold">{detail.code}</span>
                <h2 className="font-serif text-2xl font-semibold">{detail.guest_name || "Guest"}</h2>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  {detail.guest_email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{detail.guest_email}</span>}
                  {detail.guest_phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{detail.guest_phone}</span>}
                  <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{formatShortDate((detail.created_at || "").slice(0, 10))}</span>
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Status</span>
              <select value={detail.status} onChange={(e) => setStatus(detail, e.target.value)} data-testid="sub-status-select" className="rounded-lg border border-input bg-white px-2.5 py-1.5 text-sm outline-none focus:border-gold">
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="flagged">Flagged</option>
              </select>
            </div>

            <div className="my-4 border-t border-border" />

            <div className="space-y-3">
              {nonFileFields.map((f) => (
                <div key={f.id} className="grid grid-cols-3 gap-3">
                  <span className="text-sm font-medium text-muted-foreground">{f.label}</span>
                  <span className="col-span-2 text-sm">{fmtVal(detail.answers?.[f.id])}</span>
                </div>
              ))}
            </div>

            {fileFields.some((f) => (detail.files?.[f.id] || []).length) && (
              <>
                <div className="my-4 border-t border-border" />
                <p className="mb-3 inline-flex items-center gap-1.5 text-sm font-semibold"><FileText className="h-4 w-4 text-gold" /> Uploaded documents</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {fileFields.flatMap((f) => (detail.files?.[f.id] || []).map((file, i) => (
                    <div key={`${f.id}-${i}`}>
                      <p className="mb-1 text-xs font-medium text-muted-foreground">{f.label}</p>
                      <FilePreview file={file} testId={`sub-file-${f.id}-${i}`} />
                    </div>
                  )))}
                </div>
              </>
            )}

            <div className="mt-6 flex justify-between">
              <button onClick={() => downloadSubZip(detail)} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary"><Package className="h-4 w-4" /> Download all files</button>
              {canDelete && (<button onClick={() => del(detail)} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /> Delete</button>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
